/**
 * Restore access on this device — plan 029, refined.
 *
 * Reached when a parent completes a Firebase (Google) sign-in on a
 * device whose Firebase-path vault is empty. Two paths in and out
 * depending on what's already on the device:
 *
 *  - Device has a password-wrapped family key in the *password-path*
 *    vault (parent has used email + password on this browser before).
 *    Show the password field as the primary option — unlock is one
 *    step and the mnemonic can stay in a drawer. On success we
 *    additionally populate the Firebase-path vault so the next Google
 *    sign-in bypasses this screen.
 *
 *  - Device has no password-path vault. Only the mnemonic can unlock.
 *
 * Either input path ends the same way: family key in memory + wrapped
 * under a fresh non-extractable AES-GCM device key + route to the
 * dashboard.
 */

import { useEffect, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Input } from '../components/Input';
import { familyKeyFromMnemonic } from '../e2ee/mnemonic';
import {
  authSession,
  familyKeyInMemory,
  hasPasswordVault,
  loadFamilyKey,
  storeFamilyKeyFirebase,
} from '../state/session';

type Method = 'password' | 'mnemonic';

export function RestoreMnemonicRoute() {
  const location = useLocation();
  const [passwordVaultPresent, setPasswordVaultPresent] = useState<
    boolean | null
  >(null);
  const [method, setMethod] = useState<Method>('password');
  const [password, setPassword] = useState('');
  const [mnemonic, setMnemonic] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const session = authSession.value;

  useEffect(() => {
    if (session === null) return;
    hasPasswordVault(session.email).then(
      (present) => {
        setPasswordVaultPresent(present);
        setMethod(present ? 'password' : 'mnemonic');
      },
      (e: unknown) => {
        // A broken IndexedDB rejects here. Without this the page waits on
        // the vault check forever and renders blank.
        setPasswordVaultPresent(false);
        setMethod('mnemonic');
        setErr((e as Error).message);
      },
    );
  }, [session]);

  // No auth session = no Firebase callback fired. Bounce to /login.
  if (session === null) {
    location.route('/login');
    return null;
  }
  const email = session.email;

  async function onSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      if (method === 'password') {
        // loadFamilyKey throws on ChaCha20-Poly1305 tag mismatch (wrong
        // password) and returns false on empty vault. Both surface as
        // the same friendly error.
        let ok = false;
        try {
          ok = await loadFamilyKey(email, password, { remember: true });
        } catch {
          ok = false;
        }
        if (!ok) {
          setErr(
            'That password does not unlock this device. Try the recovery phrase instead.',
          );
          return;
        }
        const fk = familyKeyInMemory.value;
        if (fk === null) {
          setErr('Unlocked, but the family key vanished. Try again.');
          return;
        }
        // Populate the Firebase-path vault so the next Google sign-in
        // skips this screen entirely.
        await storeFamilyKeyFirebase(email, fk);
      } else {
        const fk = familyKeyFromMnemonic(mnemonic);
        await storeFamilyKeyFirebase(email, fk);
      }
      location.route('/dashboard');
    } catch (e) {
      setErr((e as Error).message || 'Could not restore access.');
    } finally {
      setBusy(false);
    }
  }

  // Waiting on the vault check — render nothing rather than a wrong
  // default (this only takes one microtask on real hardware).
  if (passwordVaultPresent === null) return null;

  const showPasswordPrimary = passwordVaultPresent && method === 'password';
  const showMnemonicPrimary = !passwordVaultPresent || method === 'mnemonic';

  return (
    <main class="flex min-h-screen items-center justify-center p-4">
      <Card class="w-full max-w-md space-y-4">
        <h1 class="text-2xl font-semibold">Restore access on this device</h1>
        <p class="text-sm text-surface-fg/70">
          You&apos;re signed in as <strong>{email}</strong>, but this browser
          doesn&apos;t have a wrap for Google sign-in yet.
          {passwordVaultPresent
            ? ' Unlock with your Backstop password (fastest) or use your 24-word recovery phrase.'
            : ' Enter your 24-word recovery phrase to unlock it.'}
        </p>

        <form onSubmit={onSubmit} class="space-y-3" noValidate>
          {showPasswordPrimary && (
            <label class="block space-y-1 text-sm">
              <span>Password</span>
              <Input
                type="password"
                value={password}
                onInput={(e: Event) => {
                  setPassword((e.target as HTMLInputElement).value);
                }}
                autocomplete="current-password"
                required
              />
            </label>
          )}
          {showMnemonicPrimary && (
            <label class="block space-y-1 text-sm">
              <span>Recovery phrase</span>
              <textarea
                class="min-h-32 w-full rounded border border-surface-muted/40 bg-surface p-2 font-mono text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                value={mnemonic}
                onInput={(e: Event) => {
                  setMnemonic((e.target as HTMLTextAreaElement).value);
                }}
                spellcheck={false}
                autocapitalize="none"
                required
                placeholder="word1 word2 word3 …"
              />
            </label>
          )}
          {err !== null && (
            <p class="text-sm text-danger-500" role="alert">
              {err}
            </p>
          )}
          <Button type="submit" disabled={busy}>
            {busy ? 'Unlocking…' : 'Unlock this device'}
          </Button>
        </form>

        {passwordVaultPresent && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setMethod((m) => (m === 'password' ? 'mnemonic' : 'password'));
              setErr(null);
            }}
          >
            {method === 'password'
              ? 'Use recovery phrase instead'
              : 'Use password instead'}
          </Button>
        )}

        <p class="pt-2 border-t border-line text-sm text-surface-muted">
          Lost password and phrase?{' '}
          <a href="/reset-request" class="text-signal underline">
            Reset your account
          </a>{' '}
          &mdash; you&apos;ll lose your saved data.
        </p>
      </Card>
    </main>
  );
}
